import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material'
import { Component, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Something went wrong</AlertTitle>
            <Typography variant="body2" sx={{ mb: 2 }}>
              An error occurred while rendering this component. Please try refreshing the page.
            </Typography>
            {this.state.error && (
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {this.state.error.message}
              </Typography>
            )}
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" size="small" onClick={this.handleReset}>
                Try Again
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => window.location.reload()}
                sx={{ ml: 1 }}
              >
                Refresh Page
              </Button>
            </Box>
          </Alert>
        </Box>
      )
    }

    return this.props.children
  }
}
